import React, { useState } from 'react';

import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';

const SocialLogin = ({ 
  orientation = 'vertical',
  showDivider = true,
  dividerText = 'Or continue with',
  className = ''
}) => {
  const { authError, clearError } = useAuth();
  const [loadingProvider, setLoadingProvider] = useState(null);

  const socialProviders = [
    {
      name: 'google',
      label: 'Google',
      icon: 'Chrome', // Using Chrome as closest Google icon
      bgColor: 'bg-white',
      textColor: 'text-gray-700',
      borderColor: 'border-gray-300',
      hoverBg: 'hover:bg-gray-50'
    },
    {
      name: 'facebook',
      label: 'Facebook',
      icon: 'Facebook',
      bgColor: 'bg-[#1877F2]',
      textColor: 'text-white',
      borderColor: 'border-[#1877F2]',
      hoverBg: 'hover:bg-[#166FE5]'
    },
    {
      name: 'apple',
      label: 'Apple',
      icon: 'Apple',
      bgColor: 'bg-black',
      textColor: 'text-white',
      borderColor: 'border-black',
      hoverBg: 'hover:bg-gray-800'
    },
    {
      name: 'github',
      label: 'GitHub',
      icon: 'Github',
      bgColor: 'bg-[#24292F]',
      textColor: 'text-white',
      borderColor: 'border-[#24292F]',
      hoverBg: 'hover:bg-[#1C2128]'
    }
  ];

  const handleSocialLogin = async (provider) => {
    try {
      setLoadingProvider(provider);
      if (authError) {
        clearError();
      }

      // Import authService dynamically to avoid circular dependencies
      const authService = (await import('../../services/authService')).default;
      
      const result = await authService.signInWithOAuth(provider);
      
      if (!result?.success) {
        console.error(`${provider} login failed:`, result?.error);
        // Note: OAuth errors are typically handled by redirects,
        // so we don't need to show error messages here
      }
    } catch (error) {
      console.error(`${provider} login error:`, error);
    } finally {
      setLoadingProvider(null);
    }
  };

  const containerClass = orientation === 'horizontal' ?'flex flex-wrap gap-3' :'space-y-3';

  const buttonClass = orientation === 'horizontal' ?'flex-1 min-w-[120px]' :'w-full';

  return (
    <div className={className}>
      {showDivider && (
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-text-muted font-caption">
              {dividerText}
            </span>
          </div>
        </div>
      )}

      <div className={containerClass}>
        {socialProviders.map((provider) => (
          <button
            key={provider.name}
            onClick={() => handleSocialLogin(provider.name)}
            disabled={loadingProvider === provider.name}
            className={`
              ${buttonClass}
              ${provider.bgColor}
              ${provider.textColor}
              ${provider.borderColor}
              ${provider.hoverBg}
              border-2
              rounded-lg
              px-4
              py-3
              flex
              items-center
              justify-center
              space-x-3
              font-medium
              text-sm
              transition-all
              duration-200
              focus:outline-none
              focus:ring-2
              focus:ring-primary
              focus:ring-offset-2
              disabled:opacity-60
              disabled:cursor-not-allowed
              active:scale-95
            `}
          >
            {loadingProvider === provider.name ? (
              <div className="flex items-center space-x-2">
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Connecting...</span>
              </div>
            ) : (
              <>
                <Icon name={provider.icon} size={18} color="currentColor" />
                <span>Continue with {provider.label}</span>
              </>
            )}
          </button>
        ))}
      </div>

      {/* Additional social providers for smaller screens */}
      <div className="mt-3 text-center">
        <p className="text-xs text-text-muted font-caption">
          More social login options available after sign up
        </p>
      </div>
    </div>
  );
};

export default SocialLogin;